import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { partnerSelector } from "@dashboard/config/redux";
import { fetchGetUserAddresses } from "@dashboard/modules/profile/fetchers/user-addresses.fetchers";
import { PartnerAddress } from "@dashboard/modules/profile/types/partner-addresses.types";
import { fetchDeliveryHistory } from "../fetchers/logistic.fetchers";
import { deliveryActions } from "../store/delivery.slice";
import { deliveryHistorySelector } from "../store/delivery.selectors";
import { useDeliveryToken } from "./use-delivery-token.hook";
import { DELIVERY_ROUTES } from "../delivery.routes";
import { useState } from "react";

export const useDeliveryHome = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useDeliveryToken();
  const partner = useSelector(partnerSelector);
  const history = useSelector(deliveryHistorySelector);
  const [originAddress, setOriginAddress] = useState<PartnerAddress | null>(
    null
  );
  const userId = partner?.userId;

  const loadHistory = useCallback(async () => {
    if (!token) return;
    dispatch(deliveryActions.setHistoryLoading(true));
    dispatch(deliveryActions.setHistoryError(null));
    try {
      const data = await fetchDeliveryHistory(token);
      dispatch(deliveryActions.setHistory(data));
    } catch {
      dispatch(deliveryActions.setHistoryError(t("delivery.home.loadError")));
    } finally {
      dispatch(deliveryActions.setHistoryLoading(false));
    }
  }, [token, dispatch, t]);

  const loadAddresses = useCallback(async () => {
    if (!token) return;
    try {
      const addresses = await fetchGetUserAddresses(token);
      const active =
        addresses.find((a) => a.active) || addresses[0] || null;
      setOriginAddress(active);
    } catch {
      setOriginAddress(null);
    }
  }, [token]);

  useEffect(() => {
    loadHistory();
    loadAddresses();
  }, [loadHistory, loadAddresses]);

  const goToCreateManual = useCallback(() => {
    if (userId) navigate(`/${userId}/delivery/${DELIVERY_ROUTES.CREATE}`);
  }, [navigate, userId]);

  const goToSettings = useCallback(() => {
    if (userId) navigate(`/${userId}/delivery/${DELIVERY_ROUTES.SETTINGS}`);
  }, [navigate, userId]);

  const goToDetail = useCallback(
    (orderId: number) => {
      if (userId) navigate(`/${userId}/delivery/${orderId}`);
    },
    [navigate, userId]
  );

  const goToProfile = useCallback(() => {
    if (userId) navigate(`/${userId}/profile`);
  }, [navigate, userId]);

  const breadcrumbs = [
    { label: t("delivery.breadcrumb.home") },
  ];

  return {
    history,
    originAddress,
    loadHistory,
    goToCreateManual,
    goToSettings,
    goToDetail,
    goToProfile,
    breadcrumbs,
  };
};
